import { Button } from "@/core/components/ui/button";
import { Textarea } from "@/core/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Loader2, Send, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button as DsButton } from "@woba/ds-web-next";
import { DS_COLOR, DS_TYPOGRAPHY } from "@/core/lib/ds-typography";
import { ChatInputTypes } from "../types";
import { ChatAttachIcon, ChatPlusIcon, ChatSendIcon } from "./chat-input-icons";
import { parseEmailsFromSpreadsheet } from "../utils/parseEmailsFromSpreadsheet";

const ACCEPTED_FILE_TYPES =
  ".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv";

export function ChatInput({
  value,
  disabled,
  onChange,
  onSend,
  onEnter,
  variant = "default",
  onNewConversation,
  allowedDomains = [],
  onFileParsed,
}: ChatInputTypes) {
  const [translate] = useTranslation(["core"]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleSend = () => {
    if (!disabled && value.trim()) {
      onSend();
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = variant === "action-center" ? 150 : 200;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [value, variant]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (onEnter) {
        onEnter(e as React.KeyboardEvent);
      }
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onFileParsed) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext || "")) {
      setParseError("Formato não suportado. Use CSV ou Excel (.xlsx, .xls).");
      return;
    }

    setIsParsing(true);
    setParseError(null);
    try {
      const result = await parseEmailsFromSpreadsheet(file, allowedDomains);
      onFileParsed({ ...result, fileName: file.name, fileSize: file.size });
    } catch (err) {
      setParseError(
        err instanceof Error ? err.message : "Erro ao processar o arquivo.",
      );
    } finally {
      setIsParsing(false);
    }
  };

  const isActionCenter = variant === "action-center";

  const fileInput = onFileParsed ? (
    <input
      ref={fileInputRef}
      type="file"
      accept={ACCEPTED_FILE_TYPES}
      onChange={handleFileChange}
      className="hidden"
      aria-hidden
    />
  ) : null;

  // Centro de ações: "Input Chat" do Workbase DS (Figma 6087:32916). Campo de
  // raio 24 com borda stroke-default, texto em Label/Input Xsmall, "+ Nova
  // conversa" (ghost) à esquerda e, à direita, Button Icon tertiary de anexo
  // (clipe) e Button Icon Intelligence de envio (seta), ambos de 36px.
  if (isActionCenter) {
    const sendState = disabled
      ? "loading"
      : value.trim()
        ? "enabled"
        : "disabled";
    return (
      <div className="w-full flex-shrink-0">
        {parseError && (
          <p className="mb-2 text-sm text-red-600">{parseError}</p>
        )}
        <div
          className="flex flex-col gap-2 rounded-3xl border p-2"
          style={{
            borderColor: DS_COLOR.strokeDefault,
            backgroundColor: DS_COLOR.surfaceDefault,
          }}
          data-testid="action-center-input"
        >
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              translate("chatbot-placeholder-action-center") as string
            }
            className="m-0 min-h-[52px] w-full resize-none overflow-y-auto rounded-none border-0 bg-transparent p-4 shadow-none placeholder:text-[color:var(--woba-color-content-subtle)] focus-visible:ring-0 focus-visible:ring-offset-0"
            style={{
              ...DS_TYPOGRAPHY.labelInputXsmall,
              color: DS_COLOR.contentDefault,
              maxHeight: 150,
            }}
            disabled={disabled}
            rows={1}
          />
          <div className="flex min-h-12 items-center justify-between gap-4">
            {onNewConversation ? (
              <button
                type="button"
                onClick={onNewConversation}
                className="flex min-h-9 items-center gap-2 rounded-full px-3 py-2 hover:bg-[var(--woba-color-surface-common-hover)]"
                style={{
                  ...DS_TYPOGRAPHY.labelXsmall,
                  color: DS_COLOR.contentSubtle,
                }}
              >
                <ChatPlusIcon color={DS_COLOR.contentSubtle} />
                {translate("chatbot-history-new-conversation")}
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center">
              {fileInput && (
                <span className="flex size-12 items-center justify-center">
                  {fileInput}
                  <DsButton
                    variant="ButtonIcon"
                    appearance="tertiary"
                    size="small"
                    state={disabled || isParsing ? "disabled" : "enabled"}
                    loading={isParsing}
                    tooltip="Anexar arquivo csv ou xlsx"
                    accessibilityLabel="Importar colaboradores"
                    onPress={() => fileInputRef.current?.click()}
                    icon={({ color, size }) => (
                      <ChatAttachIcon color={color} size={size} />
                    )}
                  />
                </span>
              )}
              {/* onTouchEnd preserva o envio no toque (mobile) sem depender
                  do click sintetizado, como no botão anterior. */}
              <span
                className="flex size-12 items-center justify-center"
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
              >
                <DsButton
                  variant="ButtonIconIntelligence"
                  size="small"
                  state={sendState}
                  accessibilityLabel={
                    translate("chatbot-send-message") as string
                  }
                  onPress={handleSend}
                  icon={({ color, size }) => (
                    <ChatSendIcon color={color} size={size} />
                  )}
                />
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex-shrink-0 rounded-b-[64px] bg-white p-[24px] pt-[16px]">
        {parseError && (
          <p className="mb-2 text-sm text-red-600">{parseError}</p>
        )}
        <div className="flex flex-row items-end gap-4 rounded-3xl border border-warmgray-100 p-[16px]">
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={translate("chatbot-placeholder") as string}
            className="m-0 min-h-[56px] max-h-[200px] flex-1 overflow-y-auto border-0 p-2 text-[16px] focus-visible:ring-0"
            disabled={disabled}
            rows={1}
          />
          <div className="flex gap-3">
            <span />
            <div className="flex gap-2">
              {fileInput && (
                <>
                  {fileInput}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="group h-[56px] min-w-[56px] max-w-12 shrink-0 gap-0 overflow-hidden rounded-2xl border-darkgray-700 text-darkgray-700 transition-[max-width] duration-200 ease-out hover:max-w-[250px] hover:gap-2 hover:bg-lightgray-200"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={disabled || isParsing}
                        aria-label="Importar colaboradores"
                      >
                        {isParsing ? (
                          <Loader2 className="h-6 w-6 shrink-0 animate-spin" />
                        ) : (
                          <Upload className="h-6 w-6 shrink-0" />
                        )}
                        <span className="inline-block max-w-0 overflow-hidden transition-[max-width] duration-200 ease-out group-hover:max-w-[180px]">
                          <span className="whitespace-nowrap pl-0">
                            Importar colaboradores
                          </span>
                        </span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="text-xs bg-lightgray-200 text-neutral-900 border-lightgray-300"
                    >
                      Anexar arquivo csv ou xlsx
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
              <Button
                onClick={handleSend}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                disabled={!value.trim() || disabled}
                className="min-h-[56px] min-w-[56px] shrink-0 rounded-2xl bg-darkgray-700 text-white hover:bg-darkgray-600 disabled:bg-lightgray-600 disabled:text-darkgray-200"
                type="button"
              >
                {disabled ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <Send className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
